import {
	type AggregationsAggregate,
	type SearchResponseBody,
} from '@elastic/elasticsearch/lib/api/types';
import { Comparison } from '@fpsm/cxy-components/Comparison';
import { type ElasticsearchQuery } from '@searchkit/api';
import memoize from 'lodash.memoize';

import { REPUBS } from '@cx-modules/constants';

import {
	BettingSplits,
	styles,
} from '~/components/BettingInsights/components/BettingSplits';
import {
	BetTypeFilter,
	CheckboxFilter,
	DateRefinement,
} from '~/components/BettingInsights/components/Filters';
import { Autocomplete } from '~/components/BettingInsights/components/Search';
import {
	marketMeta,
	Moneyline,
	PointSpread,
	TotalPoints,
	TotalRuns,
	WinMarket,
} from '~/components/BettingInsights/constants';
import { trackBettingSplitsBetClick } from '~/components/BettingInsights/tracking';
import {
	isOcPlusRepub,
	REPUB_COUNTRY,
} from '~/components/BettingInsights/types/index.ts';
import { Avatar } from '~/components/OddscheckerPlus/components/Avatar';
import { BetButton } from '~/components/OddscheckerPlus/components/BetButton';
import { bettingSplitsResponseFactory } from '~/configuration/betting-insights/response-factories/bettingSplitsResponseFactory.ts';
import { featureFlags } from '~/providers/feature-flag-provider';
import { type Loggable } from '~/traits/Loggable';
import { type RequestContext } from '~/types/RequestContext';
import { getOddsByFormat } from '~/utils/getOddsByFormat';

import {
	EVENT_DISPLAY_NAME,
	eventDisplayNames,
} from './constants/eventDisplayNames.ts';
import { allWNBAQuery } from './queries/allWNBAQuery';
import { eplOnlySoccerQuery } from './queries/eplOnlySoccerQuery.ts';
import { noFootballQuery } from './queries/noFootballQuery.ts';
import { supportedCategoryGroupsQuery } from './queries/supportedCategoryGroupsQuery.ts';
import { bettingSplitsQueryFactory } from './query-factories/bettingSplitsQueryFactory.ts';
import { formatLine } from './utils/formatLine';
import { getPercentageComparisonValues } from './utils/getPercentageComparisonValues';
import { sortBetsBySequence } from './utils/sortBetsBySequence';
import { BettingInsights } from './BettingInsightsConfig';
import {
	OC_CATEGORY_START,
	US_CATEGORY_END,
	US_CATEGORY_START,
} from './constants';
import {
	type BettingInsightsFacetAttribute,
	type BettingSplitsESResult,
	type BettingSplitsIndex,
} from './types';

export const bettingSplitsIndex = 'search-pbd-stats' as const;

export function bettingSplitsConfigFactory(
	this: Loggable,
	requestContext: RequestContext,
) {
	// eslint-disable-next-line @typescript-eslint/no-this-alias
	const context = this;
	return BettingInsights.withRequestContext<BettingSplitsIndex>(
		requestContext,
		{
			elasticSearch: {
				facetAttributes() {
					const repub = this.repubConfig.name;
					if (!isOcPlusRepub(repub)) {
						return [];
					}

					const repubCountry = REPUB_COUNTRY[repub];
					const areInternalSearchToolsEnabled =
						this.featureFlags.find(
							flag =>
								flag.name === featureFlags.SUBS__PLUS__INTERNAL_SEARCH_TOOLS,
						)?.enabled ?? false;

					return [
						...(areInternalSearchToolsEnabled
							? ([
									{
										attribute: 'bookmakerCode',
										field: 'bookmakerCode',
										type: 'string',
									},
								] satisfies BettingInsightsFacetAttribute<BettingSplitsIndex>[])
							: []),
						{
							attribute: `marketDisplayName`,
							field: `marketDisplayNameMap.${repubCountry}`,
							type: 'string',
						},
						{
							attribute: 'eventDisplayName',
							field: 'eventDisplayName',
							type: 'string',
						},
						{
							attribute: 'subeventId',
							field: 'subeventId',
							type: 'numeric',
						},
						{
							attribute: 'subeventStartDate',
							field: 'subeventStartDate',
							type: 'string',
						},
						{
							attribute: 'subeventStartTime',
							field: 'subeventStartTime',
							type: 'string',
						},
						{
							attribute: 'teamData.away.name',
							field: 'teamData.away.name.keyword',
							type: 'string',
						},
						{
							attribute: 'teamData.away.nickName',
							field: 'teamData.away.nickName.keyword',
							type: 'string',
						},
						{
							attribute: 'teamData.away.shortName',
							field: 'teamData.away.shortName.keyword',
							type: 'string',
						},
						{
							attribute: 'teamData.home.name',
							field: 'teamData.home.name.keyword',
							type: 'string',
						},
						{
							attribute: 'teamData.home.nickName',
							field: 'teamData.home.nickName.keyword',
							type: 'string',
						},
						{
							attribute: 'teamData.home.shortName',
							field: 'teamData.home.shortName.keyword',
							type: 'string',
						},
					];
				},
				getBaseFilters() {
					const baseFilters: ElasticsearchQuery[] = [];

					const soccerBettingSplitsEnabled =
						this.featureFlags.find(
							flag =>
								flag.name === featureFlags.SUBS__PLUS__SOCCER_BETTING_SPLITS,
						)?.enabled ?? false;
					if (!soccerBettingSplitsEnabled) {
						baseFilters.push(noFootballQuery);
					}

					const soccerEplOnlyEnabled =
						this.featureFlags.find(
							flag => flag.name === featureFlags.SUBS__PLUS__SOCCER_EPL_ONLY,
						)?.enabled ?? false;
					if (soccerEplOnlyEnabled) {
						baseFilters.push(eplOnlySoccerQuery);
					}

					// Toggle filtering out certain markets of WNBA
					const allWNBAEnabled =
						this.featureFlags.find(
							flag => flag.name === featureFlags.SUBS__PLUS__ALL_WNBA,
						)?.enabled ?? false;
					if (allWNBAEnabled) {
						baseFilters.push(allWNBAQuery);
					}

					// Limit category groups to supported list
					const newCategoryGroupsEnabled =
						this.featureFlags.find(
							flag =>
								flag.name === featureFlags.SUBS__PLUS__NEW_CATEGORY_GROUPS,
						)?.enabled ?? false;
					if (!newCategoryGroupsEnabled) {
						baseFilters.push(supportedCategoryGroupsQuery);
					}

					return baseFilters;
				},
				async beforeSearch(searchRequests) {
					const {
						request: { type },
					} = searchRequests[0];

					if (type === 'facet') {
						return searchRequests;
					}

					return searchRequests.map(bettingSplitsQueryFactory(this));
				},
				async afterSearch([searchRequest], [searchResponse, ...rest]) {
					const {
						request: { params, type },
					} = searchRequest;

					if (type === 'facet' || !params) {
						return [searchResponse, ...rest];
					}

					const { hitsPerPage = 10, page = 0 } = params || {};
					const from = !hitsPerPage || !page ? 0 : page * hitsPerPage;

					const aggregatedResponse = await bettingSplitsResponseFactory.call(
						context,
						this,
						searchResponse as SearchResponseBody<
							BettingSplitsESResult,
							Record<string, AggregationsAggregate>
						>,
						from,
						hitsPerPage,
					);
					return [aggregatedResponse, ...rest];
				},
				results() {
					return {
						attributes: [
							'betName',
							'betPercentage',
							'categoryName',
							'categoryGroupId',
							'categoryGroupName',
							'categoryId',
							'categoryName',
							'eventDisplayName',
							'eventId',
							'eventDisplayName',
							'marketDisplayName',
							'subeventId',
							'subeventName',
							'subeventStartDate',
							'subeventStartTime',
							'teamData',
						],
					};
				},
				search() {
					// const areInternalSearchToolsEnabled =
					// 	this.featureFlags.find(
					// 		flag =>
					// 			flag.name === featureFlags.SUBS__PLUS__INTERNAL_SEARCH_TOOLS,
					// 	)?.enabled ?? false;
					return {
						attributes: [
							'teamData.away.name',
							'teamData.away.nickName',
							'teamData.away.shortName',
							'teamData.home.name',
							'teamData.home.nickName',
							'teamData.home.shortName',
							// ...(areInternalSearchToolsEnabled
							// 	? ([
							// 			'betId',
							// 			'betTypeId',
							// 			'bookmakerCode',
							// 			'eventId',
							// 			'subeventId',
							// 		] as const)
							// 	: []),
						],
						highlight_attributes: [
							'teamData.away.name',
							'teamData.away.nickName',
							'teamData.away.shortName',
							'teamData.home.name',
							'teamData.home.nickName',
							'teamData.home.shortName',
							// ...(areInternalSearchToolsEnabled
							// 	? ([
							// 			'betId',
							// 			'betTypeId',
							// 			'bookmakerCode',
							// 			'eventId',
							// 			'subeventId',
							// 		] as const)
							// 	: []),
						],
					};
				},
				sorting() {
					return {
						options: [
							{
								field: 'subeventStartTime',
								label: 'Event Time',
								order: 'asc',
							},
							{
								field: 'betPercentage',
								label: '% of Bets High-Low',
								order: 'desc',
							},
							{
								field: 'betPercentage',
								label: '% of Bets Low-High',
								order: 'asc',
							},
							{
								field: 'stakePercentage',
								label: '% of Money High-Low',
								order: 'desc',
							},
							{
								field: 'stakePercentage',
								label: '% of Money Low-High',
								order: 'asc',
							},
							{
								field: 'stakeBetDiff',
								label: 'Difference High-Low',
								order: 'desc',
							},
							{
								field: 'stakeBetDiff',
								label: 'Difference Low-High',
								order: 'asc',
							},
						],
					};
				},
			},
			ui: {
				card: {
					styles,
					table: {
						body: {
							render({
								bookmakers,
								content,
								oddsFormat,
								resultIndex,
								sectionProps,
								table,
							}) {
								if (!content.bets) return null;

								const isFootball = content.categoryGroupName === 'FOOTBALL';

								return (
									<BettingSplits.Body
										bookmakers={bookmakers}
										cells={table.cells}
										content={content}
										isFootball={isFootball}
										oddsFormat={oddsFormat}
										resultIndex={resultIndex}
										sectionProps={sectionProps}
									/>
								);
							},
						},
						getContent({ result }) {
							const repub = this.repubConfig.name;
							if (!isOcPlusRepub(repub)) {
								return [];
							}

							const repubCountry = REPUB_COUNTRY[repub];
							const marketOrder = [
								PointSpread,
								Moneyline,
								WinMarket,
								TotalPoints,
								TotalRuns,
							];
							return result.markets.sort(
								(a, b) =>
									marketOrder.indexOf(a.marketDisplayNameMap[repubCountry]) -
									marketOrder.indexOf(b.marketDisplayNameMap[repubCountry]),
							);
						},
						headings: {
							content: [
								{
									key: 'left',
									render({ content }) {
										const {
											bets,
											categoryGroupName,
											marketClassification,
											marketGenericSequence,
										} = content;
										const [leftBet] = sortBetsBySequence(
											bets,
											marketClassification,
											marketGenericSequence,
										);
										const isSoccer = categoryGroupName === 'FOOTBALL';
										return (
											<>
												{content.teamData?.home?.badgeUrl &&
													content.teamData?.away?.badgeUrl && (
														<Avatar
															betName={leftBet.betName}
															homeOrAway={isSoccer ? 'home' : 'away'}
															marketClassification={marketClassification}
															marketPropType={content.marketPropType}
															teamData={content.teamData}
														/>
													)}
												<p
													data-testid="left-bet"
													style={{ fontSize: 'inherit' }}
												>
													{leftBet.betName}
												</p>
											</>
										);
									},
									renderMobile({ content }) {
										const {
											bets,
											categoryGroupName,
											marketClassification,
											marketGenericSequence,
											teamData,
										} = content;
										const [leftBet] = sortBetsBySequence(
											bets,
											marketClassification,
											marketGenericSequence,
										);
										const isSoccer = categoryGroupName === 'FOOTBALL';
										const mobileName =
											(teamData &&
												leftBet.teamType &&
												teamData[leftBet.teamType].shortName) ||
											leftBet.betName;

										return (
											<>
												{content.teamData?.home?.badgeUrl &&
													content.teamData?.away?.badgeUrl && (
														<Avatar
															betName={content.bets[0].betName}
															homeOrAway={isSoccer ? 'home' : 'away'}
															marketClassification={marketClassification}
															marketPropType={content.marketPropType}
															teamData={content.teamData}
														/>
													)}
												<p
													data-testid="left-bet-mobile"
													style={{ fontSize: 'inherit' }}
												>
													{mobileName}
												</p>
											</>
										);
									},
								},
								{
									key: 'title',
									render({ content }) {
										const repub = this.repubConfig.name;
										if (!isOcPlusRepub(repub)) {
											return null;
										}

										const repubCountry = REPUB_COUNTRY[repub];
										return (
											<p className="mainHeading" data-testid="market-header">
												{marketMeta[content.marketDisplayNameMap[repubCountry]]
													?.heading ||
													`${content.marketDisplayNameMap[repubCountry]} (O/U)`}
											</p>
										);
									},
									isMain: true,
									justifyContent: 'center',
									shouldShowIndicator: true,
								},
								{
									key: 'right',
									render({ content }) {
										const {
											bets,
											categoryGroupName,
											marketClassification,
											marketGenericSequence,
										} = content;
										const [_leftBet, _, rightBet] = sortBetsBySequence(
											bets,
											marketClassification,
											marketGenericSequence,
										);
										const isSoccer = categoryGroupName === 'FOOTBALL';
										return (
											<>
												<p
													data-testid="right-bet"
													style={{ fontSize: 'inherit' }}
												>
													{rightBet.betName}
												</p>
												{content.teamData?.home?.badgeUrl &&
													content.teamData?.away?.badgeUrl && (
														<Avatar
															betName={content.bets[0].betName}
															homeOrAway={isSoccer ? 'away' : 'home'}
															marketClassification={marketClassification}
															marketPropType={content.marketPropType}
															teamData={content.teamData}
														/>
													)}
											</>
										);
									},
									renderMobile({ content }) {
										const {
											bets,
											categoryGroupName,
											marketClassification,
											marketGenericSequence,
											teamData,
										} = content;
										const [_leftBet, _, rightBet] = sortBetsBySequence(
											bets,
											marketClassification,
											marketGenericSequence,
										);
										const isSoccer = categoryGroupName === 'FOOTBALL';
										const mobileName =
											(teamData &&
												rightBet.teamType &&
												teamData[rightBet.teamType].shortName) ||
											rightBet.betName;
										return (
											<>
												<p
													data-testid="right-bet-mobile"
													style={{ fontSize: 'inherit', textAlign: 'end' }}
												>
													{mobileName}
												</p>
												{content.teamData?.home?.badgeUrl &&
													content.teamData?.away?.badgeUrl && (
														<Avatar
															betName={content.bets[0].betName}
															homeOrAway={isSoccer ? 'away' : 'home'}
															marketClassification={marketClassification}
															marketPropType={content.marketPropType}
															teamData={content.teamData}
														/>
													)}
											</>
										);
									},
									justifyContent: 'flex-end',
								},
							],
						},
						cells: [
							{
								key: 'left',
								render({
									bookmakers,
									content: {
										bets,
										categoryName,
										marketClassification,
										marketDisplayNameMap,
										marketGenericSequence,
										marketPropType,
										subeventName,
										teamData,
									},
									oddsFormat,
								}) {
									const repub = this.repubConfig.name;
									if (!isOcPlusRepub(repub)) {
										return null;
									}

									const repubCountry = REPUB_COUNTRY[repub];
									const sortBets = memoize(() =>
										sortBetsBySequence(
											bets,
											marketClassification,
											marketGenericSequence,
										),
									);
									const gameBets = sortBets();
									const [leftBet, middleBet] = gameBets;

									const bookmaker = bookmakers[leftBet.bookmakerCode];
									if (!bookmaker) {
										return null;
									}

									const hasThreeWayResult = middleBet !== undefined;

									const marketDisplayName = marketDisplayNameMap[repubCountry];

									const line = hasThreeWayResult
										? teamData.home.shortName
										: leftBet.line && formatLine(leftBet.line);
									const onClick = memoize(() => {
										trackBettingSplitsBetClick({
											bookmaker,
											bet: leftBet,
											categoryName,
											marketDisplayName,
											marketPropType,
											subeventName,
										});
									});

									return (
										<BetButton
											aria-label={`Place a bet on ${leftBet.betName} ${marketDisplayName} with ${bookmaker.bookmakerName} at odds of ${leftBet.oddsAmerican}`}
											bet={leftBet}
											bookieColour={bookmaker?.brandColour || 'black'}
											bookieLogo={bookmaker?.logoSmall}
											bookieName={bookmaker?.bookmakerName}
											buttonStyles={{
												flexDirection: 'column',
												alignItems: 'flex-start',
											}}
											label={
												hasThreeWayResult
													? ''
													: `Best ${
															marketMeta[marketDisplayName]?.info ||
															`${leftBet.betName} Line`
														}`
											}
											line={line}
											odds={getOddsByFormat(leftBet, oddsFormat)}
											onClick={onClick}
											repub={this.repubConfig.name}
											shouldHideLabelOnDesktop={hasThreeWayResult}
											shouldHideLabelOnMobile
										/>
									);
								},
							},
							{
								key: 'comparison',
								render({
									content: {
										bets,
										marketClassification,
										marketDisplayNameMap,
										marketGenericSequence,
									},
								}) {
									const repub = this.repubConfig.name;
									if (!isOcPlusRepub(repub)) {
										return null;
									}

									const repubCountry = REPUB_COUNTRY[repub];
									const sortBets = memoize(() =>
										sortBetsBySequence(
											bets,
											marketClassification,
											marketGenericSequence,
										),
									);
									const [leftBet, centerBet, rightBet] = sortBets();

									const marketDisplayName = marketDisplayNameMap[repubCountry];
									const betInformation = `% of ${marketDisplayName}, ${leftBet.betName} v ${rightBet.betName},`;

									const getPercentageComparison = memoize(() =>
										getPercentageComparisonValues(
											leftBet.betPercentage,
											rightBet.betPercentage,
											centerBet?.betPercentage || undefined,
										),
									);
									const betValues = getPercentageComparison();

									const getStakeComparison = memoize(() =>
										getPercentageComparisonValues(
											leftBet.stakePercentage,
											rightBet.stakePercentage,
											centerBet?.stakePercentage || undefined,
										),
									);
									const stakeValues = getStakeComparison();

									return (
										<>
											<Comparison
												aria-label={`${betInformation} Bets with ${betValues.leftValue}% on the left and ${betValues.rightValue}% on the right.`}
												comparisonLabel={`% of ${marketDisplayName} Bets`}
												tooltipText={
													marketMeta[marketDisplayName]?.betsTooltipText
												}
												{...betValues}
											/>
											<Comparison
												aria-label={`${betInformation} stakes with ${stakeValues.leftValue}% on the left and ${stakeValues.rightValue}% on the right.`}
												comparisonLabel={`% of ${marketDisplayName} Money`}
												tooltipText={
													marketMeta[marketDisplayName]?.moneyTooltipText
												}
												{...stakeValues}
											/>
										</>
									);
								},
								isMain: true,
								isFullWidthMobile: true,
								order: -1,
								justifyContent: 'center',
							},
							{
								key: 'right',
								render({
									bookmakers,
									content: {
										bets,
										categoryName,
										marketClassification,
										marketDisplayNameMap,
										marketGenericSequence,
										marketPropType,
										subeventName,
										teamData,
									},
									oddsFormat,
								}) {
									const repub = this.repubConfig.name;
									if (!isOcPlusRepub(repub)) {
										return null;
									}

									const repubCountry = REPUB_COUNTRY[repub];
									const sortBets = memoize(() =>
										sortBetsBySequence(
											bets,
											marketClassification,
											marketGenericSequence,
										),
									);
									const gameBets = sortBets();
									const [_leftBet, middleBet, rightBet] = gameBets;
									const hasThreeWayResult = middleBet !== undefined;

									const line = hasThreeWayResult
										? teamData.away.shortName
										: rightBet.line && formatLine(rightBet.line);

									const bookmaker = bookmakers[rightBet.bookmakerCode];
									if (!bookmaker) {
										return null;
									}

									const marketDisplayName = marketDisplayNameMap[repubCountry];

									const onClick = memoize(() => {
										trackBettingSplitsBetClick({
											bookmaker,
											bet: rightBet,
											categoryName,
											marketDisplayName,
											marketPropType,
											subeventName,
										});
									});

									return (
										<BetButton
											aria-label={`Place a bet on ${rightBet.betName} ${marketDisplayName} with ${bookmaker.bookmakerName} at odds of ${rightBet.oddsAmerican}`}
											bet={rightBet}
											bookieColour={bookmaker?.brandColour || 'black'}
											bookieLogo={bookmaker?.logoSmall}
											bookieName={bookmaker?.bookmakerName}
											buttonStyles={{
												flexDirection: 'column',
												alignItems: 'flex-end',
											}}
											label={
												hasThreeWayResult
													? ''
													: `Best ${
															marketMeta[marketDisplayName]?.info ||
															`${rightBet.betName} Line`
														}`
											}
											line={line}
											odds={getOddsByFormat(rightBet, oddsFormat)}
											onClick={onClick}
											repub={this.repubConfig.name}
											shouldHideLabelOnDesktop={hasThreeWayResult}
											shouldHideLabelOnMobile
										/>
									);
								},
								start: 3,
								justifyContent: 'flex-end',
							},
							{
								key: 'center',
								render({
									bookmakers,
									content: {
										bets,
										categoryName,
										marketClassification,
										marketDisplayNameMap,
										marketGenericSequence,
										marketPropType,
										subeventName,
									},
									oddsFormat,
								}) {
									const repub = this.repubConfig.name;
									if (!isOcPlusRepub(repub)) {
										return null;
									}

									const repubCountry = REPUB_COUNTRY[repub];
									const sortBets = memoize(() =>
										sortBetsBySequence(
											bets,
											marketClassification,
											marketGenericSequence,
										),
									);
									const [_, __, centerBet] = sortBets();

									const marketDisplayName = marketDisplayNameMap[repubCountry];

									const bookmaker = bookmakers[centerBet.bookmakerCode];
									if (!bookmaker) {
										return null;
									}

									const onClick = memoize(() => {
										trackBettingSplitsBetClick({
											bookmaker,
											bet: centerBet,
											categoryName,
											marketDisplayName,
											marketPropType,
											subeventName,
										});
									});
									return (
										<BetButton
											aria-label={`Place a bet on ${centerBet.betName} ${marketDisplayName} with ${bookmaker.bookmakerName} at odds of ${centerBet.oddsAmerican}`}
											bet={centerBet}
											bookieColour={bookmaker?.brandColour || 'black'}
											bookieLogo={bookmaker?.logoSmall}
											bookieName={bookmaker?.bookmakerName}
											buttonStyles={{
												flexDirection: 'column',
												alignItems: 'flex-end',
											}}
											line="DRAW"
											odds={getOddsByFormat(centerBet, oddsFormat)}
											onClick={onClick}
											repub={this.repubConfig.name}
											shouldHideLabelOnDesktop
											shouldHideLabelOnMobile
										/>
									);
								},
								justifyContent: 'center',
							},
						],
						shouldToggle: true,
						showAdditionalTitle: true,
					},
					title: {
						getFormatOverride({ result }) {
							if (result.categoryGroupName === 'FOOTBALL') {
								return '{eventDisplayName} | {teamData.home.shortName} vs {teamData.away.shortName} | {subeventStartTime}';
							}
						},
						defaultFormat:
							'{eventDisplayName} | {teamData.away.shortName} @ {teamData.home.shortName} | {subeventStartTime}',
					},
					wrapper: ({ children, config, content, result }) => (
						<BettingSplits config={config} content={content} result={result}>
							{children}
						</BettingSplits>
					),
				},
				initialUiState() {
					const soccerBettingSplitsEnabled =
						this.featureFlags.find(
							flag =>
								flag.name === featureFlags.SUBS__PLUS__SOCCER_BETTING_SPLITS,
						)?.enabled ?? false;

					return {
						hitsPerPage: 10,
						refinementList: {
							eventDisplayName: [
								this.repubConfig.name === REPUBS.US.name ||
								!soccerBettingSplitsEnabled
									? 'NFL'
									: 'EPL',
							],
						},
					};
				},
				refinements: {
					clearAll: {
						excludedAttributes: [],
					},
					components: [
						{
							attribute: 'bookmakerCode',
							getLabel() {
								return 'Bookmaker (Internal use only)';
							},
							render({ bookmakers }) {
								const areInternalSearchToolsEnabled =
									this.featureFlags.find(
										flag =>
											flag.name ===
											featureFlags.SUBS__PLUS__INTERNAL_SEARCH_TOOLS,
									)?.enabled ?? false;

								if (!areInternalSearchToolsEnabled) {
									return null;
								}

								return (
									<CheckboxFilter
										attribute={this.attribute}
										isDisabled={this.isWalled}
										label={this.getLabel()}
										sortBy={['name:asc']}
										transformFieldLabel={({ count, label }) => {
											const bookmaker = bookmakers[label];
											return `${bookmaker?.bookmakerName ?? label} (${count})`;
										}}
									/>
								);
							},
						},
						{
							attribute: 'marketDisplayName',
							getLabel() {
								return 'Bet Type';
							},
							render() {
								return (
									<BetTypeFilter
										attribute={this.attribute}
										isDisabled={this.isWalled}
										label={this.getLabel()}
										sortBy={['name:asc']}
									/>
								);
							},
						},
						{
							attribute: 'subeventStartDate',
							getLabel() {
								return 'Dates';
							},
							render() {
								return (
									<DateRefinement
										attribute={this.attribute}
										isDisabled={this.isWalled}
										label={this.getLabel()}
									/>
								);
							},
						},
					],
				},
				results: {
					categories() {
						const isSoccerBettingSplitsEnabled =
							this.featureFlags.find(
								flag =>
									flag.name === featureFlags.SUBS__PLUS__SOCCER_BETTING_SPLITS,
							)?.enabled ?? false;

						return {
							attribute: EVENT_DISPLAY_NAME,
							labels: eventDisplayNames[this.repubConfig.name],
							order: {
								start:
									this.repubConfig.name === REPUBS.US.name ||
									!isSoccerBettingSplitsEnabled
										? US_CATEGORY_START
										: OC_CATEGORY_START,
								...(this.repubConfig.name === REPUBS.US.name && {
									end: US_CATEGORY_END,
								}),
							},
						};
					},
					propsName: 'games',
					labelledBy: '#bet-name',
				},
				search: {
					render({ searchAttributes }) {
						const textSearchEnabled =
							this.featureFlags.find(
								f => f.name === featureFlags.SUBS__PLUS__TEXT_SEARCH,
							)?.enabled ?? false;

						if (!textSearchEnabled) {
							return null;
						}

						return (
							<Autocomplete
								disabled={this.isWalled}
								indexName={bettingSplitsIndex}
								repub={this.repubConfig.name}
								searchAttributes={searchAttributes}
							/>
						);
					},
				},
				sorting: {
					canSort({ refinements }) {
						return (
							refinements.filter(r => r.attribute === 'marketDisplayName')
								.length > 0
						);
					},
				},
			},
		},
	);
}
